"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function DialogueForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center animate-in fade-in duration-700">
        <div className="size-16 rounded-lg border border-primary/30 flex items-center justify-center mb-6 bg-primary/10">
          <svg className="size-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl md:text-3xl mb-4 text-white font-bold tracking-tight">
          Assessment Scheduled
        </h3>
        <p className="text-muted-foreground text-lg mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-secondary hover:text-white rounded-md px-8 h-12 transition-all">
          Return
        </Button>
      </div>;
  }
  const inputClasses = "border-x-0 border-t-0 border-b border-border rounded-md px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent text-lg py-3 transition-colors placeholder:text-muted-foreground/30 h-auto";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="full_name" rules={{
      required: "Please provide your name."
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default text-xl text-muted-foreground font-medium">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Alexander Wright" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/50 text-sm">
              Enter your name for the personalized AI report.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "An email address is required.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address."
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default text-xl text-muted-foreground font-medium">
              Business Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="alex@company.com" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/50 text-sm">
              We will deliver your 48-hour assessment here.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default text-xl text-muted-foreground font-medium">
              Company Name
            </FormLabel>
            <FormControl>
              <Input placeholder="RecoupTime Industries" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/50 text-sm">
              Helps Olivia prepare for your specific industry.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
        message: "Please enter a valid phone number."
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default text-xl text-muted-foreground font-medium">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/50 text-sm">
              Required for your 15-minute call with Olivia.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please share your message."
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default text-xl text-muted-foreground font-medium">
              Current Bottlenecks
            </FormLabel>
            <FormControl>
              <textarea placeholder="Briefly describe where your team spends the most manual time..." className={`flex min-h-[160px] w-full resize-none outline-none ${inputClasses}`} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/50 text-sm">
              This helps us identify your "Hidden Hours" faster.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-lg font-bold tracking-tight transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 size-5" />
              Processing...
            </> : "Secure Your Assessment"}
        </Button>
      </div>
    </Form>;
}
export default function SecureYourAssessment() {
  return <section data-section-id="245581" id="secure-your-assessment" className="bg-background text-foreground min-h-screen relative flex items-center justify-center py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-background via-background to-primary/5">
      <div className="w-full max-w-[600px] mx-auto relative z-10">
        <div className="mb-16 text-center">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">
            Your business is running you. Let’s flip that.
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-default max-w-md mx-auto leading-relaxed">
            The gateway for high-level inquiries and bespoke AI consultations.
          </p>
        </div>

        <FormProvider formKey="assessment_form_recoup_time" formId="assessment_form_recoup_time" sectionName="Secure Your Assessment" defaultValues={{
        full_name: "",
        email: "",
        company: "",
        phone: "",
        message: ""
      }} submitText="Secure Your Assessment" successMessage="Your request has been received. Olivia will reach out within the next 4 hours to schedule your call.">
          <DialogueForm />
        </FormProvider>
      </div>
    </section>;
}